import {Mutations} from "vuex-smart-module";
import AccessControlState from "@/store/modules/access-control/access-control-state";
import AccessControlDeviceData from "@/http/data/access-control-device-data";
import AccessControlCardData from "@/http/data/access-control-card-data";
import AccessControlEventPageData from "@/http/data/access-control-event-page-data";
import AccessControlDoorData from "@/http/data/access-control-door-data";
import {AccessControlStatus} from "@/store/modules/access-control/access-control-state";

type IndexedDoors = Record<number, AccessControlDoorData>;

export default class AccessControlMutations extends Mutations<AccessControlState> {
    setDevices(devices: AccessControlDeviceData[]): void {
        this.state.devices = devices;
    }

    setDevicesCache(payload: {
        buildingId: string;
        devices: AccessControlDeviceData[];
        status: AccessControlStatus;
        expiresAt: number;
    }): void {
        this.state.devicesByBuildingId = {...this.state.devicesByBuildingId,
            [payload.buildingId]: payload.devices
        };

        this.state.accessControlStatusByBuildingId = {
            ...this.state.accessControlStatusByBuildingId,
            [payload.buildingId]: payload.status
        };

        this.state.devicesCacheExpiresAtByBuildingId = {
            ...this.state.devicesCacheExpiresAtByBuildingId,
            [payload.buildingId]: payload.expiresAt
        };
    }

    setDeviceDetails(device: AccessControlDeviceData): void {
        this.state.devices = this.state.devices.map(existingDevice =>
            existingDevice.device_id === device.device_id ? device : existingDevice
        );

        this.state.devicesByBuildingId = Object.fromEntries(
            Object.entries(this.state.devicesByBuildingId).map(
                ([buildingId, devices]: [string, AccessControlDeviceData[]]): [string, AccessControlDeviceData[]] => [
                    buildingId,
                    devices.map(existingDevice => existingDevice.device_id === device.device_id ? device : existingDevice)
                ]
            )
        );

        this.state.deviceDetailsById = {
            ...this.state.deviceDetailsById,
            [device.device_id]: device
        };
    }

    setCardNumbersForDevice(payload: { deviceId: number; cardNumbers: number[] }): void {
        this.state.cardNumbersByDeviceId = {
            ...this.state.cardNumbersByDeviceId,
            [payload.deviceId]: payload.cardNumbers
        };
    }

    setDoorsForDevice(payload: { deviceId: number; doors: AccessControlDoorData[] }): void {
        const doorDetails = payload.doors.reduce(
            (indexedDoors: IndexedDoors, door: AccessControlDoorData): IndexedDoors => {
                indexedDoors[door.door_id] = door;
                return indexedDoors;
            },
            {}
        );

        this.state.doorsByDeviceId = {
            ...this.state.doorsByDeviceId,
            [payload.deviceId]: payload.doors
        };

        this.state.doorDetailsByDeviceId = {
            ...this.state.doorDetailsByDeviceId,
            [payload.deviceId]: {
                ...this.state.doorDetailsByDeviceId[payload.deviceId],
                ...doorDetails
            }
        };
    }

    setDoorDetails(payload: { deviceId: number; door: AccessControlDoorData }): void {
        const existingDeviceDoors = this.state.doorDetailsByDeviceId[payload.deviceId] ?? {};
        const existingDoors = this.state.doorsByDeviceId[payload.deviceId] ?? [];
        const updatedDoors = [
            ...existingDoors.filter(door => door.door_id !== payload.door.door_id),
            payload.door
        ].sort((left, right) => left.door_id - right.door_id);

        this.state.doorsByDeviceId = {
            ...this.state.doorsByDeviceId,
            [payload.deviceId]: updatedDoors
        };

        this.state.doorDetailsByDeviceId = {
            ...this.state.doorDetailsByDeviceId,
            [payload.deviceId]: {
                ...existingDeviceDoors,
                [payload.door.door_id]: payload.door
            }
        };
    }

    setCardDetails(payload: { deviceId: number; card: AccessControlCardData }): void {
        const existingDeviceCards = this.state.cardDetailsByDeviceId[payload.deviceId] ?? {};

        this.state.cardDetailsByDeviceId = {
            ...this.state.cardDetailsByDeviceId,
            [payload.deviceId]: {
                ...existingDeviceCards,
                [payload.card.card_number]: payload.card
            }
        };
    }

    removeCard(payload: { deviceId: number; cardNumber: number }): void {
        const existingNumbers = this.state.cardNumbersByDeviceId[payload.deviceId] ?? [];
        const filteredNumbers = existingNumbers.filter(cardNumber => cardNumber !== payload.cardNumber);

        const deviceCards = this.state.cardDetailsByDeviceId[payload.deviceId] ?? {};
        const remainingCards = {...deviceCards};

        delete remainingCards[payload.cardNumber];

        this.state.cardNumbersByDeviceId = {
            ...this.state.cardNumbersByDeviceId,
            [payload.deviceId]: filteredNumbers
        };

        this.state.cardDetailsByDeviceId = {
            ...this.state.cardDetailsByDeviceId,
            [payload.deviceId]: remainingCards
        };
    }

    setEventsPage(payload: { deviceId: number; page: number; eventsPage: AccessControlEventPageData }): void {
        const existingPages = this.state.eventsPagesByDeviceId[payload.deviceId] ?? {};

        this.state.eventsPagesByDeviceId = {
            ...this.state.eventsPagesByDeviceId,
            [payload.deviceId]: {
                ...existingPages,
                [payload.page]: payload.eventsPage
            }
        };
    }

    setSelectedDeviceId(deviceId: null | number): void {
        this.state.selectedDeviceId = deviceId;
    }

    setSelectedCardNumber(cardNumber: null | number): void {
        this.state.selectedCardNumber = cardNumber;
    }

    setSelectedDoorId(doorId: null | number): void {
        this.state.selectedDoorId = doorId;
    }

    setSelectedEventsPageNumber(page: number): void {
        this.state.selectedEventsPageNumber = page;
    }

    setAccessControlStatus(status: AccessControlState["accessControlStatus"]): void {
        this.state.accessControlStatus = status;
    }

    setIsInitialized(value: boolean): void {
        this.state.isInitialized = value;
    }
}
